import { pick, isString, extend, get, isArray, filter } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { getDealDetails, fillLocationForDeal } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { CampaignSubTypeMapping, CampaignSubTypeTextMapping } from '@bingads-webui-campaign-react/constants';
import { getVerifiedTrackingSettingsRead } from '@bingads-webui-campaign-react/third-party-measurement';
import { modelToViewModelForConversionGoals, bulkModelToViewModelForConversionGoals } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { modelToViewModelForDayTimeTargets } from './ad-group-criterions';
import { modelToViewModelForLocationTargets, bulkModelToViewModelForLocationTargets } from './campaign-criterions';
import { modelToViewModelForCallToActionSettings } from './call-to-action-settings';
import { RemovedDeviceBidAdjustment } from '../common/constants';
import { modelToViewModelForDateSetting, viewModelToBulkModelForDateSetting, bulkModelToViewModelForDateSetting } from '../common/date-settings';

const whitelistedSchemaPropertyKeysForModel = [
  'Budget', 'Id', 'Name', 'Status', 'TimeZone', 'BidStrategyName', 'BudgetId',
  'AdScheduleUseSearcherTimeZone',
  'DeliveryStatus', 'DeliveryStatusDetails', 'CampaignType', 'CampaignSubType',
  'StartDate', 'EndDate',
  'UseCampaignLevelDates',
  'IsBidLandscapeAvailable',
  'BiddingScheme',
  'CampaignSettings', 'DisplayBiddingStrategyType',
  'DisplayBiddingStrategyMaxCpc', 'DisplayBiddingStrategyTargetCpa', 'Languages',
  'BaseCampaignName',
  'VerifiedTrackingData',
  'CampaignObjectiveSetting',
  'TrackingUrlTemplate', 'FinalUrlSuffix', 'UrlCustomParameters', 'DisclaimerAdsEnabled',
];

export const viewModelToModelForCampaign = (campaignViewModel) => {
  let data = {
    ...pick(campaignViewModel.data, whitelistedSchemaPropertyKeysForModel),
    CampaignSubType: CampaignSubTypeTextMapping[campaignViewModel.data.CampaignSubType],
  };

  data = extend(data, viewModelToBulkModelForDateSetting({ i18n: null, viewModel: campaignViewModel }));

  if (campaignViewModel.data && campaignViewModel.data.Status === 'UserPaused') {
    data.Status = 'Paused'; // if we send back userpaused mt treats it as active, we explicitly need to send paused
  }

  return data;
};

export const bulkModelToViewModelForCampaign = ({
  campaignModel,
  originalCampaignModel,
  bundleCountries,
}) => {
  let data = {
    ...pick(campaignModel, whitelistedSchemaPropertyKeysForModel),
    CampaignSubType: CampaignSubTypeMapping[campaignModel.CampaignSubType],
  };

  data = extend(data, {
    deal: get(originalCampaignModel, ['data', 'deal']),
  });
  if (data.Id && isString(data.Id)) {
    // eslint-disable-next-line no-param-reassign
    data.Id = parseInt(data.Id, 10);
  }

  if (data.CampaignType === 'Audience') {
    data = extend(data, bulkModelToViewModelForConversionGoals({
      campaignModel,
      originalCampaignViewModel: originalCampaignModel,
    }));
  }

  if (data.StartDate) {
    data = extend(data, bulkModelToViewModelForDateSetting({
      model: campaignModel,
      i18n: null,
    }));
  }

  if (campaignModel && campaignModel.CampaignCriterions && isArray(campaignModel.CampaignCriterions)) {
    data = extend(data, bulkModelToViewModelForLocationTargets({
      locationTargetsModel: campaignModel.CampaignCriterions,
      bundleCountries,
    }));
  }

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };

  return campaignViewModel;
};

const readCampaign = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .get({
      select: ['Budget', 'CampaignConversionGoal/Goals'],
    })
    .toPromise();

export const loadCampaign = (cid, aid, campaignId) =>
  readCampaign(cid, aid, campaignId);

export const modelToViewModelForCampaign = ({
  campaignModel,
  dealDetails,
  dayTimeCriterionsModel = [],
  locationCriterionsModel,
  campaignDeviceCriterions,
  bundleCountries,
  isAudienceMMACTAOptOutEnabled = false,
  isAudienceMMACTADefaultOptOutEnabled = false,
  exclusions,
}) => {
  let data = {
    ...pick(campaignModel, [...whitelistedSchemaPropertyKeysForModel, 'ImportProperties']),
    CampaignSubType: CampaignSubTypeMapping[campaignModel.CampaignSubType],
  };

  if (dealDetails) {
    data = extend(data, { deal: dealDetails });
  }

  if (exclusions) {
    data = extend(data, { exclusions });
  }

  if (data.CampaignType === 'Audience') {
    const audienceSettings = filter(
      get(data, ['CampaignSettings'], []),
      setting => setting['@odata.type'] === '#Model.AudienceSettings'
    );
    const audienceSetting = get(audienceSettings, [0], {});
    data = extend(data, getVerifiedTrackingSettingsRead(audienceSetting));
  }

  // adding conversion goals for display ads, video ads and native ads
  if (data.CampaignType === 'Audience' &&
    (data.CampaignSubType === CampaignSubTypeMapping.AudienceStatic ||
      data.CampaignSubType === CampaignSubTypeMapping.DisplayAds ||
      data.CampaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds
    )) {
    data = extend(data, modelToViewModelForConversionGoals(campaignModel));
  }

  if (data.CampaignType === 'Audience' && data.CampaignSubType === CampaignSubTypeMapping.AudienceStatic) {
    data = extend(data, modelToViewModelForDayTimeTargets({ model: dayTimeCriterionsModel }));

    if (isAudienceMMACTAOptOutEnabled) {
      data = extend(data, modelToViewModelForCallToActionSettings(campaignModel, isAudienceMMACTADefaultOptOutEnabled));
    }
  }

  if (locationCriterionsModel) {
    data = extend(data, modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      bundleCountries,
      campaignId: campaignModel.Id,
    }));
  }

  if (campaignModel.StartDate) {
    data = extend(data, modelToViewModelForDateSetting({
      model: campaignModel,
      i18n: null,
    }));
  }

  if (campaignDeviceCriterions) {
    const initialCampaignLevelDeviceTargets = campaignDeviceCriterions.filter(criterion => criterion.Criterion.Type === 'Device')
      .map(criterion => ({
        Id: criterion.Id,
        criterion: criterion.Criterion.DeviceName,
        bidAdjustment: criterion.CriterionBid.Multiplier,
      }));

    const campaignLevelDeviceTargets = initialCampaignLevelDeviceTargets
      .filter(device => device.bidAdjustment !== RemovedDeviceBidAdjustment && device.bidAdjustment !== undefined)
      .map(criterion => criterion.criterion);

    data = extend(data, {
      initialCampaignLevelDeviceTargets,
      campaignLevelDeviceTargets,
    });
  }

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return campaignViewModel;
};

export {
  getDealDetails,
  fillLocationForDeal,
};
